import { useState, useEffect, useRef } from "react";
import ListOfMigrationTable from "../DBMigration/ListOfMigrationTable";
import Edit from "../migrations/Edit";
import LogModelForDbMigration from "./logModelForDBMigration";
import ViewModelForDbMigration from "./viewModelForDbMigration";
import CreateDatabase from "../DBMigration/CreateDatabase";
import { showToast } from "../Buttons/Toaster";

function DBMigration({ activeTab, setActiveTab }) {
  const [showEdit, setShowEdit] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [logshow, logsetShow] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedJobName, setSelectedJobName] = useState(null);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState(null);
  const [viewshow, viewsetShow] = useState(false);
  const [jobProgressData, setJobProgressData] = useState(null);
  const [showCreateDatabase, setShowCreateDatabase] = useState(false);
  const [databaseStep, setDatabaseStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Centralized log state management
  const [logs, setLogs] = useState([]);
  const [isLogLoading, setIsLogLoading] = useState(false);
  const [fileProcessCompleted, setFileProcessCompleted] = useState(false);
  const [jobProcessCompleted, setJobProcessCompleted] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [logWebSocket, setLogWebSocket] = useState(null);

  const [progressLoading, setProgressLoading] = useState(false);
  const [progressError, setProgressError] = useState(null);

  const progressIntervalRef = useRef(null);
  const isProgressFetchingRef = useRef(false);
  const lastFetchTimeRef = useRef(0);
  const [forceTooltipJobId, setForceTooltipJobId] = useState(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const socketRef = useRef(null);

  // Modify the fetchJobProgress function to return a promise
  const fetchJobProgress = async (jobId) => {
    return new Promise((resolve, reject) => {
      const authToken = sessionStorage.getItem("access_token");
      if (!authToken || !jobId) {
        console.error("Missing token or jobId");
        setProgressError("Missing token or jobId");
        reject("Missing token or jobId");
        return;
      }

      if (socketRef.current) {
        socketRef.current.close();
      }

      setProgressLoading(true);
      setProgressError(null);
const wsBaseProgress = process.env.REACT_APP_API_URL_DATA_MIGRATION
      socketRef.current = new WebSocket(
           `${wsBaseProgress}/ws/migration_progress/${jobId}?token=${authToken}`
      );

      socketRef.current.onopen = () => {
        console.log(`WebSocket connected for job ${jobId}`);
        setProgressLoading(false);
        resolve();
      };

      socketRef.current.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          if (message.event === "migration_progress_update") {
            setJobProgressData(message.data);
          } else if (message.event === "error") {
            setProgressError(message.message);
            socketRef.current?.close();
            reject(message.message);
          }
        } catch (err) {
          console.error("WebSocket message parsing failed:", err);
          reject(err);
        }
      };

      socketRef.current.onerror = (err) => {
        console.error("WebSocket error:", err);
        setProgressError("WebSocket connection error");
        setProgressLoading(false);
        socketRef.current?.close();
        reject(err);
      };

      socketRef.current.onclose = () => {
        console.log("WebSocket closed for job:", jobId);
      };
    });
  };

  // Modify handleViewProgress to properly await the connection
  const handleViewProgress = async (jobId) => {
    setSelectedJobId(jobId);
    try {
      await fetchJobProgress(jobId); // Wait for connection to establish
      connectToLogs(jobId);
      setDatabaseStep(4);
      setShowCreateDatabase(true);
    } catch (error) {
      console.error("Failed to establish WebSocket:", error);
      showToast("Failed to connect to progress updates", "error");
    }
  };

  // Optional cleanup
  const closeProgressSocket = () => {
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
    }
  };

  const clearProgressInterval = () => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
      console.log("Progress interval cleared");
    }
  };

  const startProgressPolling = (jobId) => {
    clearProgressInterval();

    progressIntervalRef.current = setInterval(async () => {
      console.log("Polling progress...");
      await fetchJobProgress(jobId);
    }, 10000);

    console.log("Progress polling started");
  };

  const connectToLogs = (jobId, fileId = null) => {
    if (logWebSocket) {
      logWebSocket.close();
    }

    // Reset log state
    setLogs([]);
    setIsLogLoading(true);
    setFileProcessCompleted(false);
    setJobProcessCompleted(false);
    setIsStreaming(false);

    const token = sessionStorage.getItem("access_token");
    if (!token) {
      console.error("Token missing");
      setIsLogLoading(false);
      return;
    }
const wsBaseMigrationLogs = process.env.REACT_APP_API_URL_DATA_MIGRATION
    const ws = new WebSocket( `${wsBaseMigrationLogs}/ws/logs/${jobId}`);

    ws.onopen = () => {
      ws.send(JSON.stringify({ token, file_id: fileId }));
    };

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      const { event: type, data } = msg;

      if (msg.error) {
        console.error("WebSocket error:", msg.error);
        ws.close();
        return;
      }

      if (type === "full_update") {
        setLogs(data);
        setIsLogLoading(false);
        setIsStreaming(true);
        checkForCompletion(data, fileId);
      }

      if (type === "incremental_update") {
        setLogs((prevLogs) => {
          const updatedLogs = [...prevLogs, ...data];
          checkForCompletion(updatedLogs, fileId);
          return updatedLogs;
        });
        setIsLogLoading(false);
        setIsStreaming(true);
      }
    };

    ws.onerror = (err) => {
      console.error("WebSocket error:", err);
      setIsStreaming(false);
      setIsLogLoading(false);
    };

    ws.onclose = () => {
      console.log("WebSocket closed");
      setIsStreaming(false);
    };

    setLogWebSocket(ws);
  };

  // Centralized completion check logic
  const checkForCompletion = (logEntries, fileId) => {
    if (fileId) {
      const hasFileCompletion = logEntries.some(
        (log) =>
          log.message.toLowerCase().includes("process completed for table") ||
          log.message.toLowerCase().includes("process failed for table")
      );
      if (hasFileCompletion) {
        setFileProcessCompleted(true);
        if (logWebSocket) {
          logWebSocket.close();
        }
      }
    } else {
      const hasJobCompletion = logEntries.some(
        (log) =>
          (log.message &&
            log.message.toLowerCase().includes("process completed for job")) ||
          log.message.toLowerCase().includes("process failed for job")
      );
      if (hasJobCompletion) {
        setJobProcessCompleted(true);
        // **NEW: Clear progress polling when job completes**
        clearProgressInterval();
        if (logWebSocket) {
          logWebSocket.close();
        }
      }
    }
  };

  // **UPDATED: Cleanup function**
  useEffect(() => {
    return () => {
      if (logWebSocket) {
        logWebSocket.close();
      }
      clearProgressInterval();
    };
  }, [logWebSocket]);

  // Event handlers
  const handleJobSave = () => setRefreshKey((prev) => prev + 1);

  const handleLogShow = (jobId, jobName, fileId = null, fileName = null) => {
    setSelectedJobId(jobId);
    setSelectedJobName(jobName);
    setSelectedFileId(fileId);
    setSelectedFileName(fileName);
    connectToLogs(jobId, fileId);
    logsetShow(true);
  };

  // **UPDATED: Use centralized fetchJobProgress with force flag**
  const handleViewShow = async (jobId) => {
    setSelectedJobId(jobId);
    await fetchJobProgress(jobId, true); // Force fetch for view
    viewsetShow(true);
  };

  // **UPDATED: Use centralized fetchJobProgress with force flag**
  // const handleViewProgress = async (jobId) => {
  //   setSelectedJobId(jobId)
  //   await fetchJobProgress(jobId, true) // Force fetch for view progress
  //   connectToLogs(jobId)
  //   setDatabaseStep(4)
  //   setShowCreateDatabase(true)
  // }

  // **UPDATED: Simplified handleMigrationStarted function**
  const handleMigrationStarted = async (jobId) => {
    console.log("Migration started for job:", jobId);

    // Fetch initial progress data
    await fetchJobProgress(jobId, true); // Force initial fetch

    // Connect to logs
    connectToLogs(jobId);

    // Start progress polling
    startProgressPolling(jobId);
  };

  // **NEW: Handle modal close to cleanup intervals**
  const handleModalClose = () => {
    clearProgressInterval();
    if (logWebSocket) {
      logWebSocket.close();
    }
  };

  return (
    <div className="tab_content">
      <div className="page-content">
        <div className="container-fluid">
          <ListOfMigrationTable
            setShowEdit={setShowEdit}
            setSelectedJob={setSelectedJob}
            refreshKey={refreshKey}
            setRefreshKey={setRefreshKey}
            onLogClick={handleLogShow}
            onViewClick={handleViewShow}
            onViewProgress={handleViewProgress}
            setShowCreateDatabase={setShowCreateDatabase}
            setDatabaseStep={setDatabaseStep}
            onEditJob={setSelectedJob}
            forceTooltipJobId={forceTooltipJobId}
            tooltipVisible={tooltipVisible}
          />
        </div>
      </div>

      <div
        className={`rightbar-overlay ${
          logshow || viewshow || showCreateDatabase || showEdit
            ? "d-block"
            : "d-none"
        }`}
      ></div>

      {logshow && (
        <LogModelForDbMigration
          logshow={logshow}
          logsetShow={logsetShow}
          jobId={selectedJobId}
          fileId={selectedFileId}
          jobName={selectedJobName}
          fileName={selectedFileName}
          logs={logs}
          isLogLoading={isLogLoading}
          fileProcessCompleted={fileProcessCompleted}
          jobProcessCompleted={jobProcessCompleted}
          isStreaming={isStreaming}
        />
      )}

      {viewshow && (
        <ViewModelForDbMigration
          viewshow={viewshow}
          viewsetShow={viewsetShow}
          jobId={selectedJobId}
          jobProgressData={jobProgressData}
          fetchJobProgress={fetchJobProgress}
          progressLoading={progressLoading}
          progressError={progressError}
          onClose={handleModalClose}
        />
      )}

      {showCreateDatabase && (
        <CreateDatabase
          show={showCreateDatabase}
          setShow={setShowCreateDatabase}
          DatabaseStep={databaseStep}
          setDatabaseStep={setDatabaseStep}
          onJobExecuted={() => {
            setRefreshKey((prev) => prev + 1);
            if (selectedJobId) {
              handleMigrationStarted(selectedJobId);
            }
          }}
          jobDetails={selectedJob}
          isViewingProgress={databaseStep === 4}
          jobProgressData={jobProgressData}
          fetchJobProgress={fetchJobProgress}
          progressLoading={progressLoading}
          progressError={progressError}
          logs={logs}
          isLogLoading={isLogLoading}
          jobProcessCompleted={jobProcessCompleted}
          isStreaming={isStreaming}
          onMigrationStarted={handleMigrationStarted}
          onClose={handleModalClose}
        />
      )}
      {showEdit && (
        <Edit
          show={showEdit}
          setShow={setShowEdit}
          job={selectedJob}
          onSave={handleJobSave}
          apiEndpoint={`${process.env.REACT_APP_API_URL_DATA_MIGRATION}/edit_job`}
        />
      )}
    </div>
  );
}

export default DBMigration;
